import { useCallback, useMemo, useState } from 'react'
import type { SetStateAction } from 'react'

import { isEqualYear } from '@/utils/date'

import type { Blog } from '@/types/blog'

export const useBlogList = (
  blogList: readonly Blog[],
  countPerPage: number
) => {
  const [selectedYears, setSelectedYears] = useState<readonly string[]>([])
  const [selectedTags, setSelectedTags] = useState<readonly string[]>([])
  const [currentPage, setCurrentPage] = useState(1)

  const filteredBlogList = useMemo(
    () =>
      blogList.filter(({ tag, publishDate }) => {
        const yearFilter =
          selectedYears.length > 0
            ? selectedYears.some((year) => isEqualYear(publishDate, year))
            : true
        const tagFilter =
          selectedTags.length > 0
            ? tag
                .map(({ name }) => name)
                .some((name) => selectedTags.includes(name))
            : true

        return yearFilter && tagFilter
      }),

    [blogList, selectedTags, selectedYears]
  )

  const displayBlogList = useMemo(() => {
    const start = countPerPage * (currentPage - 1)
    const end = start + countPerPage
    const list = filteredBlogList.slice(
      start,
      filteredBlogList.length < end ? undefined : end
    )

    return list
  }, [currentPage, filteredBlogList, countPerPage])

  const changeSelectedYear = useCallback((selectedYear: string) => {
    setSelectedYears((currentYears) => {
      if (currentYears.includes(selectedYear)) {
        return currentYears.filter((year) => year !== selectedYear)
      }

      return [...currentYears, selectedYear]
    })
    setCurrentPage(1)
  }, [])

  const changeSelectedTag = useCallback((text: string) => {
    setSelectedTags((currentTags) => {
      if (currentTags.includes(text)) {
        return currentTags.filter((tag) => tag !== text)
      }

      return [...currentTags, text]
    })
    setCurrentPage(1)
  }, [])

  const handleAllTags = useCallback(() => {
    setSelectedTags([])
    setCurrentPage(1)
  }, [])

  const handleAllYears = useCallback(() => {
    setSelectedYears([])
    setCurrentPage(1)
  }, [])

  const transitionPage = useCallback((value: SetStateAction<number>) => {
    setCurrentPage(value)
    window.scrollTo({ top: 0 })
  }, [])

  return {
    selectedTags,
    selectedYears,
    displayBlogList,
    totalCount: filteredBlogList.length,
    currentPage,
    changeSelectedYear,
    changeSelectedTag,
    transitionPage,
    handleAllTags,
    handleAllYears,
  }
}
